<template>
  <!--
    Privacy Rights Request form. Public, no auth required. Posts to
    POST /v1/legal/privacy_request. CCPA's two-methods rule requires
    more than email — this is the second method. Also serves GDPR /
    UK GDPR / PIPEDA / Quebec Law 25 / other US state law requests.
  -->
  <div class="legal-page">
    <div class="legal-page-card">
      <div class="legal-page-header">
        <h1 class="legal-page-title">Privacy Rights Request</h1>
        <p class="legal-page-meta">
          Use this form to exercise your rights under CCPA/CPRA, GDPR, UK GDPR, PIPEDA, Quebec Law 25, or any other applicable privacy law. You can also email <a href="mailto:support@kiey.com">support@kiey.com</a>.
        </p>
      </div>

      <div v-if="submitted" class="legal-page-success" role="status" aria-live="polite">
        <h2>Request received</h2>
        <p>Thanks — your privacy request has been logged. Your tracking ID is:</p>
        <p class="tracking-id">{{ tracking_id }}</p>
        <p>You'll receive a confirmation email at <strong>{{ submitted_email }}</strong>. We may follow up to verify your identity before fulfilling the request, as required by applicable law.</p>
        <p>Expected response timeframes:</p>
        <ul class="response-times">
          <li>CCPA / CPRA (California): 45 days</li>
          <li>GDPR (EU/EEA) and UK GDPR: 30 days</li>
          <li>PIPEDA (Canada): 30 days</li>
          <li>Quebec Law 25: 30 days</li>
        </ul>
        <button type="button" class="btn-primary" @click="reset">Submit another request</button>
      </div>

      <form v-else class="pr-form" @submit.prevent="onSubmit" novalidate>
        <fieldset class="pr-fieldset">
          <legend>1. Type of request</legend>
          <div class="pr-radio-grid">
            <label v-for="opt in request_types" :key="opt.value" class="pr-radio">
              <input
                type="radio"
                name="request_type"
                :value="opt.value"
                v-model="form.request_type"
                required
              />
              <span>{{ opt.label }}</span>
            </label>
          </div>
        </fieldset>

        <fieldset class="pr-fieldset">
          <legend>2. Your jurisdiction</legend>
          <select v-model="form.jurisdiction" required class="pr-input">
            <option value="" disabled>Select…</option>
            <option v-for="opt in jurisdictions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </fieldset>

        <fieldset class="pr-fieldset">
          <legend>3. Your contact information</legend>
          <label class="pr-label">
            Full name <span class="pr-required">*</span>
            <input v-model.trim="form.full_name" type="text" required maxlength="200" class="pr-input" />
          </label>
          <label class="pr-label">
            Email <span class="pr-required">*</span>
            <input v-model.trim="form.email" type="email" required class="pr-input" />
          </label>
          <label class="pr-label">
            Phone (optional)
            <input v-model.trim="form.phone" type="tel" maxlength="40" class="pr-input" />
          </label>
        </fieldset>

        <fieldset class="pr-fieldset">
          <legend>4. Details (optional)</legend>
          <label class="pr-label">
            Description / details
            <textarea v-model="form.description" maxlength="5000" rows="4" class="pr-input"></textarea>
          </label>
        </fieldset>

        <fieldset class="pr-fieldset">
          <legend>5. Authorized agent (CCPA)</legend>
          <label class="pr-checkbox">
            <input type="checkbox" v-model="form.on_behalf" />
            <span>I am submitting this request on behalf of someone else</span>
          </label>
          <div v-if="form.on_behalf" class="pr-agent-fields">
            <label class="pr-label">
              Subject's name
              <input v-model.trim="form.subject_name" type="text" maxlength="200" class="pr-input" />
            </label>
            <label class="pr-label">
              Subject's email
              <input v-model.trim="form.subject_email" type="email" class="pr-input" />
            </label>
            <label class="pr-label">
              Written authorization (file)
              <input type="file" accept=".pdf,.png,.jpg,.jpeg" @change="onFile" class="pr-file" />
              <small v-if="auth_file_name">{{ auth_file_name }}</small>
              <small v-else>If you don't have a file ready, you can email it to support@kiey.com referencing your tracking ID.</small>
            </label>
          </div>
        </fieldset>

        <p class="pr-disclaimer">
          We may follow up to verify your identity before fulfilling the request, as required by applicable law. Submitting this form does not waive your right to email <a href="mailto:support@kiey.com">support@kiey.com</a> or contact us by mail.
        </p>

        <div v-if="error" class="pr-error" role="alert">{{ error }}</div>

        <button type="submit" class="btn-primary" :disabled="submitting">
          {{ submitting ? "Submitting…" : "Submit request" }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import config from "@/config/constants.js";

export default {
  name: "PrivacyRequest",
  data() {
    return {
      submitting: false,
      submitted: false,
      submitted_email: "",
      tracking_id: "",
      error: "",
      auth_file_name: "",
      // We don't actually upload the file in this MVP — the form note
      // tells the user to email it referencing their tracking ID.
      // Wiring up signed-URL upload is a follow-up.
      form: {
        request_type: "",
        jurisdiction: "",
        full_name: "",
        email: "",
        phone: "",
        description: "",
        on_behalf: false,
        subject_name: "",
        subject_email: "",
        gpc_detected: false,
      },
      request_types: [
        { value: "access", label: "Access / Know what data you have about me" },
        { value: "delete", label: "Delete my data" },
        { value: "correct", label: "Correct my data" },
        { value: "opt_out_sale_share", label: "Opt out of sale or sharing" },
        { value: "opt_out_automated_decision", label: "Opt out of automated decision-making" },
        { value: "limit_sensitive_pi", label: "Limit use of sensitive personal information" },
        { value: "withdraw_consent", label: "Withdraw consent (GDPR)" },
        { value: "data_portability", label: "Data portability (GDPR / Quebec)" },
        { value: "other", label: "Other / not listed" },
      ],
      jurisdictions: [
        { value: "california", label: "California (CCPA / CPRA)" },
        { value: "other_us_state", label: "Other US state" },
        { value: "eu_eea", label: "EU / EEA (GDPR)" },
        { value: "uk", label: "UK (UK GDPR)" },
        { value: "canada", label: "Canada (PIPEDA)" },
        { value: "quebec", label: "Quebec (Law 25)" },
        { value: "other", label: "Other" },
      ],
    };
  },
  mounted() {
    // Detect Global Privacy Control signal — honored automatically per
    // CCPA. We pass it through to the backend for audit purposes.
    try {
      this.form.gpc_detected = !!(navigator && navigator.globalPrivacyControl);
    } catch (e) {
      this.form.gpc_detected = false;
    }
  },
  methods: {
    onFile(e) {
      const file = e && e.target && e.target.files && e.target.files[0];
      this.auth_file_name = file ? file.name : "";
      // We don't upload from the MVP — the note in the template tells
      // the user to email the authorization document referencing the
      // tracking ID after submit.
    },
    async onSubmit() {
      this.error = "";
      if (!this.form.request_type) { this.error = "Please pick a request type."; return; }
      if (!this.form.jurisdiction) { this.error = "Please pick your jurisdiction."; return; }
      if (!this.form.full_name) { this.error = "Please enter your full name."; return; }
      if (!this.form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
        this.error = "Please enter a valid email address.";
        return;
      }
      if (this.form.on_behalf && this.form.subject_email
        && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.subject_email)) {
        this.error = "Subject email is not valid.";
        return;
      }

      this.submitting = true;
      try {
        const payload = {
          request_type: this.form.request_type,
          jurisdiction: this.form.jurisdiction,
          full_name: this.form.full_name,
          email: this.form.email,
          phone: this.form.phone || "",
          description: this.form.description || "",
          on_behalf: !!this.form.on_behalf,
          subject_name: this.form.subject_name || "",
          subject_email: this.form.subject_email || "",
          gpc_detected: !!this.form.gpc_detected,
          source: "admin_kiey_com",
        };
        const res = await axios.post(config.api_url + "legal/privacy_request", payload);
        const data = (res && res.data && res.data.data) || {};
        if (data.tracking_id) {
          this.tracking_id = data.tracking_id;
          this.submitted_email = this.form.email;
          this.submitted = true;
        } else {
          this.error = "Unexpected response. Please try again.";
        }
      } catch (e) {
        const status = e && e.response && e.response.status;
        if (status === 429) {
          this.error = "Too many submissions from this address. Please try again later.";
        } else if (e && e.response && e.response.data && e.response.data.errors) {
          this.error = "Please review the form and try again.";
        } else {
          this.error = "Could not submit your request. Please email support@kiey.com.";
        }
      } finally {
        this.submitting = false;
      }
    },
    reset() {
      this.submitted = false;
      this.tracking_id = "";
      this.submitted_email = "";
      this.auth_file_name = "";
      this.form = {
        request_type: "",
        jurisdiction: "",
        full_name: "",
        email: "",
        phone: "",
        description: "",
        on_behalf: false,
        subject_name: "",
        subject_email: "",
        gpc_detected: this.form.gpc_detected,
      };
    },
  },
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: #f5f7fb;
  padding: 32px 16px;
  box-sizing: border-box;
}
.legal-page-card {
  max-width: 720px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(6, 29, 71, 0.06);
  padding: 36px 40px;
  box-sizing: border-box;
}
.legal-page-header {
  border-bottom: 1px solid #e4e8f1;
  padding-bottom: 18px;
  margin-bottom: 24px;
}
.legal-page-title {
  color: #061d47;
  font-family: "Roboto-Medium", Arial, sans-serif;
  font-size: 26px;
  font-weight: 600;
  margin: 0 0 8px;
}
.legal-page-meta {
  color: #4d566c;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}
.legal-page-meta a { color: #29488d; }
.pr-fieldset {
  border: 0;
  padding: 0;
  margin: 0 0 22px;
}
.pr-fieldset legend {
  font-weight: 700;
  color: #061d47;
  font-size: 15px;
  margin-bottom: 10px;
  padding: 0;
}
.pr-radio-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}
.pr-radio {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #2d2d2d;
  padding: 8px 10px;
  border: 1px solid #e4e8f1;
  border-radius: 6px;
}
.pr-radio:hover { background: #f5f7fb; }
.pr-radio input[type="radio"] { margin-top: 3px; }
.pr-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #4d566c;
  margin-bottom: 12px;
}
.pr-required { color: #c0392b; }
.pr-input,
select.pr-input,
textarea.pr-input {
  display: block;
  width: 100%;
  margin-top: 6px;
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  color: #2d2d2d;
  background: #fff;
  border: 1px solid #d3d8e3;
  border-radius: 6px;
  box-sizing: border-box;
}
.pr-input:focus,
select.pr-input:focus,
textarea.pr-input:focus {
  outline: none;
  border-color: #29488d;
  box-shadow: 0 0 0 3px rgba(41, 73, 141, 0.12);
}
textarea.pr-input { resize: vertical; min-height: 80px; }
.pr-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #2d2d2d;
}
.pr-agent-fields {
  margin-top: 12px;
  padding: 12px 14px;
  background: #f5f7fb;
  border-radius: 6px;
}
.pr-file { margin-top: 6px; font-size: 13px; }
.pr-file + small { display: block; margin-top: 4px; color: #6a7388; font-size: 12px; }
.pr-disclaimer {
  font-size: 12px;
  color: #6a7388;
  line-height: 1.5;
  margin: 0 0 18px;
}
.pr-disclaimer a { color: #29488d; }
.pr-error {
  background: #fbecec;
  color: #b13c3c;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 14px;
}
.btn-primary {
  display: inline-block;
  background: #29488d;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border: 0;
  border-radius: 6px;
  padding: 12px 22px;
  cursor: pointer;
}
.btn-primary:hover { background: #1f3771; }
.btn-primary:disabled { background: #8b97b7; cursor: not-allowed; }
.legal-page-success h2 {
  color: #061d47;
  font-size: 20px;
  margin: 0 0 12px;
}
.legal-page-success p {
  font-size: 14px;
  color: #2d2d2d;
  line-height: 1.55;
  margin: 0 0 12px;
}
.legal-page-success .tracking-id {
  display: inline-block;
  background: #f0f3f9;
  color: #061d47;
  font-weight: 700;
  font-family: "SF Mono", Menlo, Monaco, Consolas, monospace;
  font-size: 18px;
  padding: 8px 14px;
  border-radius: 6px;
}
.response-times {
  margin: 8px 0 18px;
  padding-left: 22px;
  font-size: 14px;
  color: #2d2d2d;
}
.response-times li { margin-bottom: 4px; }

@media (max-width: 720px) {
  .legal-page-card { padding: 24px 18px; }
  .legal-page-title { font-size: 22px; }
}
</style>
